import React from 'react';
import Tag from '@app/component/tag/Tag';
import { PromotionHotelRow, PromotionNight, PromotionOverview } from '@app/model/promotion/PromotionOverview';
import { PreferredTierBadge }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';
import { isHotelPaid, nightTintClass, nightTooltip, promotionPillLabel } from './PromotionsPage.utilities';

const TOOLTIP_ID = 'promotions-tooltip';
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CELL_PITCH = 7;
const MIN_TICK_GAP = 6;
const NAME_WIDTH = 'w-52';

type MonthTick = { statDate: string; label: string; offset: number };

const monthTicks = (nights: PromotionNight[]): MonthTick[] => {
	const firsts = nights
		.map((night, index) => ({ night, index }))
		.filter(({ night }) => night.statDate.slice(8, 10) === '01');
	const ticks = firsts.map(({ night, index }) => ({
		statDate: night.statDate,
		label: MONTH_SHORT[Number(night.statDate.slice(5, 7)) - 1],
		offset: index * CELL_PITCH,
	}));
	const opening = nights[0];
	if (opening != null && (firsts.length === 0 || firsts[0].index >= MIN_TICK_GAP)) {
		ticks.unshift({
			statDate: opening.statDate,
			label: MONTH_SHORT[Number(opening.statDate.slice(5, 7)) - 1],
			offset: 0,
		});
	}
	return ticks;
};

// The name takes the free width and truncates. The badge slot has a fixed width at the end of
// the column, so the Preferred badges of all rows line up whatever the length of the name.
const HotelName: React.FunctionComponent<{ row: PromotionHotelRow }> = ({ row }) =>
	<span className={`flex ${NAME_WIDTH} shrink-0 items-center gap-1.5`}>
		<span className={`min-w-0 flex-1 truncate text-sm ${row.main ? 'font-semibold' : 'font-medium'} text-gray-900`}>
			{row.hotelName}
		</span>
		{row.main && <Tag state="info">You</Tag>}
		<span className="flex w-4 shrink-0 justify-center">
			{(row.preferredTier === 'PREFERRED' || row.preferredTier === 'PREFERRED_PLUS') &&
				<PreferredTierBadge plus={row.preferredTier === 'PREFERRED_PLUS'} />}
		</span>
	</span>;

const GroupTitle: React.FunctionComponent<{ dot: string; children: React.ReactNode }> = ({ dot, children }) =>
	<div className="flex items-center gap-1.5 px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
		<span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
		{children}
	</div>;

const StripRow: React.FunctionComponent<{ row: PromotionHotelRow }> = ({ row }) =>
	<div className={`flex items-center gap-3 rounded px-2 py-1 ${row.main ? 'bg-sky-50' : ''}`}>
		<HotelName row={row} />
		<span className="flex shrink-0 gap-px">
			{row.nightly.map(night =>
				<span key={night.statDate}
					data-tooltip-id={TOOLTIP_ID}
					data-tooltip-content={nightTooltip(row.hotelName, night)}
					className={`block h-5 w-1.5 ring-1 ring-inset ring-gray-200 ${nightTintClass(night)}`} />)}
		</span>
		<span className="w-9 shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-gray-500">
			{row.share90 == null ? '—' : `${Math.round(row.share90)}%`}
		</span>
		<span className="flex shrink-0 gap-1">
			{row.types.slice(0, 2).map(type =>
				<Tag key={type.code} state={type.bookingFunded ? 'none' : 'success'}>
					{promotionPillLabel(type.code)}
				</Tag>)}
		</span>
	</div>;

const NightStrip: React.FunctionComponent<{
	overview: PromotionOverview
	sourceNote: string
	includeBookingFunded: boolean
}> = ({ overview, sourceNote, includeBookingFunded }) => {
	const first = overview.rows[0];
	const nightCount = first == null ? 0 : first.nightly.length;
	const hotelPaid = overview.rows.filter(isHotelPaid);
	const fundedOnly = overview.rows.filter(row => !isHotelPaid(row));

	return <section className="overflow-hidden rounded-lg bg-white shadow">
		<div className="flex flex-wrap items-start gap-4 px-5 py-4">
			<div className="min-w-0 flex-1">
				<h2 className="text-base font-semibold text-gray-900">
					Deals competitors have scheduled — next {nightCount} nights
				</h2>
				<p className="mt-1 text-xs text-gray-400">
					{overview.meta.competitorsWithDeals90} of {overview.meta.competitorCount} competitors have deals
					{' '}in the next 90 nights
					{overview.meta.typicalDepth90 != null && ` · typical ~${Math.round(overview.meta.typicalDepth90)}% off`}
					{` · ${overview.meta.preferredCompetitorCount} of ${overview.meta.competitorCount} are Preferred partners`}
					{includeBookingFunded && ' · incl. Booking-funded'}
				</p>
			</div>
		</div>
		<div className="overflow-x-auto border-t border-gray-300 px-5 pb-2">
			<div className="min-w-max">
				<div className="relative ml-52 h-4">
					{monthTicks(first?.nightly ?? []).map(tick =>
						<span key={tick.statDate} className="absolute whitespace-nowrap text-xs text-gray-400"
							style={{ left: `${tick.offset}px` }}>
							{tick.label}
						</span>)}
				</div>
				<GroupTitle dot="bg-green-500">Hotel-paid deals</GroupTitle>
				{hotelPaid.map(row => <StripRow key={row.hotelId} row={row} />)}
				{fundedOnly.length > 0 && <>
					<GroupTitle dot="bg-blue-500">Booking.com-funded only</GroupTitle>
					{fundedOnly.map(row => <StripRow key={row.hotelId} row={row} />)}
				</>}
				{overview.excludedHotelNames.length > 0 &&
					<div className="flex items-center gap-3 px-2 pb-1 pt-2 text-xs text-gray-400">
						<span className={`${NAME_WIDTH} shrink-0 font-semibold uppercase tracking-wide`}>No deals in 90 nights</span>
						<span>{overview.excludedHotelNames.join(' · ')}</span>
					</div>}
			</div>
		</div>
		<div className="flex flex-wrap items-center gap-5 border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
			<span>One column is one night.</span>
			<span className="inline-flex items-center gap-1.5">
				<span className="inline-block h-3 w-3 bg-white ring-1 ring-inset ring-gray-200" />no deal
			</span>
			<span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 bg-gray-300" />sold out</span>
			<span className="inline-flex items-center gap-1.5">
				<span className="inline-block h-3 w-3 bg-emerald-50" />
				<span className="inline-block h-3 w-3 bg-emerald-200" />
				<span className="inline-block h-3 w-3 bg-emerald-400" />deeper discount
			</span>
			<span>A night with a minimum stay shows the deal of that stay.</span>
			<span className="inline-flex items-center gap-1.5">
				<PreferredTierBadge plus={false} />Booking.com Preferred partner
				<PreferredTierBadge plus />Preferred Plus
			</span>
		</div>
		<div className="px-5 pb-4"><p className="mt-2 text-xs leading-4 text-gray-400">{sourceNote}</p></div>
	</section>;
};

export default NightStrip;
